"use client";

import { useRef, memo, useCallback } from "react";
import Link from "next/link";
import "devicon/devicon.min.css";
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";

const techs = [
    "python",
    "java",
    "javascript",
    "typescript",
    "react",
    "go",
    "rust",
    "swift",
    "kotlin",
    "postgresql",
    "mongodb",
    "graphql",
];

const getTechLabel = (tech: string) => {
    switch (tech) {
        case "graphql":
            return "GraphQL";
        default:
            return tech.charAt(0).toUpperCase() + tech.slice(1);
    }
};

const getIconClassName = (tech: string) => {
    switch (tech) {
        case "react":
            return "devicon-react-original";
        default:
            return `devicon-${tech}-plain`;
    }
};

const TechItem = memo(({ tech, locale }: { tech: string; locale: string }) => {
    const href = getPathWithLocale(`/technologies/${tech.toLowerCase()}`, locale as "ru" | "en");
    return (
        <Link
            href={href}
            className="group block shrink-0 snap-start glass-matte border border-white/10 p-3 text-center hover:brightness-110 transition-all duration-300"
            style={{ width: 120, height: 145 }}
        >
            <div className="flex h-full flex-col items-center justify-center">
                {/* Цветные фирменные иконки видны сразу: на тач-устройствах ховера
                    нет, поэтому белый вариант оставлял их неокрашенными навсегда. */}
                <i className={`${getIconClassName(tech)} colored block text-4xl md:text-5xl text-white/85`} />
                <span className="mt-2 text-xs text-white/70">{getTechLabel(tech)}</span>
            </div>
        </Link>
    );
});
TechItem.displayName = 'TechItem';

/**
 * Полоса технологического стека. Заголовок и вводный абзац отсюда убраны: на
 * странице уже есть H1 «Технологии» с описанием, а второй такой же блок только
 * дублировал первый экран. Секцию и контейнер задаёт страница через <PageSection>.
 */
export default function TechnologiesSection() {
    const scroller = useRef<HTMLDivElement>(null);
    const locale = useLocale();
    const isEn = !locale.startsWith("ru");

    const scrollBy = useCallback((delta: number) => {
        const el = scroller.current;
        if (!el) return;
        el.scrollTo({ left: Math.max(0, el.scrollLeft + delta), behavior: "smooth" });
    }, []);

    return (
        <div className="relative">
            <div
                ref={scroller}
                className="overflow-x-auto py-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
                <div className="flex w-max gap-3 md:gap-4 snap-x snap-mandatory">
                    {techs.map((tech) => (
                        <TechItem key={tech} tech={tech} locale={locale} />
                    ))}
                </div>
            </div>

            <div className="hidden md:flex justify-end gap-3 mt-4">
                <button
                    aria-label={isEn ? "Scroll left" : "Прокрутить влево"}
                    onClick={() => scrollBy(-200)}
                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/40 hover:bg-white/5 hover:text-white transition-all duration-200"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <button
                    aria-label={isEn ? "Scroll right" : "Прокрутить вправо"}
                    onClick={() => scrollBy(200)}
                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/40 hover:bg-white/5 hover:text-white transition-all duration-200"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>
    );
}
